import React, { ReactElement } from 'react'
import { Flex, Text, Image } from 'theme-ui'
import { Button, Svg } from 'ui'
import { heroSectionData } from 'components/FreeDns/sections/Hero/heroSectionData'
import StarsWrapper, { bigStarStyles } from 'components/Organization/HomePage/StarsWrapper'
import LogoBgIcon from 'images/organization/homepage/sections/solutions/logo-bg.svg'
import HeroChipIllustration from 'images/freeDNS/hero-chip-illustration-mobile.png'
import Checkmark from 'images/freeDNS/hero-section-checkmark.svg'
import { CONTROLDText } from 'ui/CONTROLD'
import { navigate } from 'gatsby'
import { useAppSelector } from 'store/hooks'
import { PlansSteps } from 'components/Plans/usePlanWizardSteps'
import { TextWithBreakpointVariant } from 'ui/TextWithBreakpointVariant'

export default function Hero(): ReactElement {
  const isLoggedIn = useAppSelector(s => !!s.persistData.sessionToken)

  return (
    <Flex
      className="ios-scrolling-glitches-fix"
      sx={{
        position: 'relative',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        py: ['4rem', '10rem'],
        px: ['1.6rem', '2.4rem', '11.2rem'],
        zIndex: 'zIndex5',
        '@media screen and (min-width: 1120px) and (max-width: 1330px)': {
          px: '4.6rem',
        },
      }}
    >
      <Flex
        className="ios-scrolling-glitches-fix"
        sx={{
          position: 'absolute',
          width: '100vw',
          height: ['21.2rem', '32rem'],
          top: ['1rem', '3rem'],
          backgroundColor: ['turquoise20', 'aquamarine8'],
          filter: 'blur(150px)',
        }}
      />
      <Svg
        svg={LogoBgIcon}
        descriptiveText="Decorative background logo"
        sx={{
          visibility: ['hidden', 'hidden', 'visible'],
          position: 'absolute',
          top: '-50rem',
          right: 0,
        }}
      />
      <StarsWrapper
        starsSx={[
          {
            top: '-5rem',
            left: '25rem',
            'g > circle': {
              fill: 'mauve',
            },
          },
          {
            top: '28.5rem',
            left: '-1.5rem',
            ...bigStarStyles,
          },
          {
            top: '-7.5rem',
            right: '7.5rem',
            ...bigStarStyles,
          },
          {
            top: '40rem',
            right: '-10rem',
            ...bigStarStyles,
          },
          {
            top: '42.5rem',
            left: '75rem',
          },
        ]}
      >
        <Flex
          sx={{
            flexDirection: ['column', 'row'],
            width: ['100%', '100%'],
            maxWidth: '128.8rem',
            justifyContent: 'space-between',
            zIndex: 'zIndex1',
            '@media screen and (min-width: 1500px)': {
              maxHeight: '49.8rem',
            },
          }}
        >
          <Flex
            sx={{
              gap: ['2.4rem', '1.6rem', '4rem'],
              flexDirection: 'column',
              textAlign: ['center', 'inherit'],
              my: ['2.4rem', '2.85rem'],
              justifyContent: 'center',
              width: ['100%', '50%'],
              maxWidth: ['100%', '64rem'],
            }}
          >
            <Flex
              sx={{
                flexDirection: 'column',
                gap: ['1.6rem', '2.4rem'],
              }}
            >
              {' '}
              <Text
                as="h1"
                sx={{
                  display: 'inline-block',
                  fontSize: '4rem',
                  fontWeight: '600',
                  color: 'aliceBlue',
                }}
              >
                Free and Public DNS Servers From {CONTROLDText}
              </Text>
              <Text
                variant="size21Weight600Line138"
                sx={{
                  display: 'inline-block',
                  color: 'aliceBlue',
                }}
              >
                Block Ads, Malware, and Stay Safe Online
              </Text>
            </Flex>
            <Image
              sx={{ display: ['flex', 'none'], width: '100%', height: '100%' }}
              src={HeroChipIllustration}
              aria-label="DNS server chip illustration for mobile"
            />
            <Flex
              sx={{
                gap: '1.6rem',
                flexDirection: 'column',
                maxWidth: ['none', '85%'],
              }}
            >
              {heroSectionData.map((item, index) => (
                <Flex
                  key={index}
                  sx={{
                    gap: '1.2rem',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <Svg
                    sx={{
                      flexShrink: 0,
                    }}
                    svg={Checkmark}
                    descriptiveText={`Feature checkmark: ${item.text}`}
                  />
                  <TextWithBreakpointVariant
                    variant={[
                      'size16Weight600Line138',
                      'size16Weight600Line138',
                      'size17Weight600Line138',
                    ]}
                    sx={{
                      display: 'inline-block',
                      textAlign: 'left',
                      color: 'lightSteelBlue',
                    }}
                  >
                    {item.text}
                  </TextWithBreakpointVariant>
                </Flex>
              ))}
            </Flex>
            <Flex
              sx={{
                gap: '1.6rem',
                alignContent: ['center', 'flex-start'],
                justifyContent: ['center', 'flex-start'],
                flexDirection: ['column', 'row', 'row'],
                '@media screen and (min-width: 768px) and (max-width: 980px)': {
                  width: '20rem',
                  flexWrap: 'wrap',
                },
              }}
            >
              <Button
                ariaLabel={isLoggedIn ? 'Go to Dashboard' : 'Get More Control'}
                name="Free DNS CTA Button"
                data-testid="freedns-cta-button"
                sx={{
                  whiteSpace: 'nowrap',
                  '@media screen and (min-width: 768px) and (max-width: 980px)': {
                    width: '100%',
                  },
                }}
                variant="roundedSecondary"
                onClick={() =>
                  navigate(
                    isLoggedIn
                      ? '/dashboard'
                      : `/plans?step=${PlansSteps.CREATE_ACCOUNT}&fullControl=true`,
                  )
                }
              >
                <Text>{isLoggedIn ? 'Go to Dashboard' : 'Get More Control'}</Text>
              </Button>
            </Flex>
          </Flex>
          <Image
            sx={{
              flexShrink: 0,
              display: ['none', 'flex'],
              width: '56.8rem',
              height: '49.8rem',
              alignSelf: 'center',
              '@media screen and (min-width: 768px) and (max-width: 980px)': {
                width: '40rem',
                height: '35rem',
              },
            }}
            src={HeroChipIllustration}
            aria-label="DNS server chip illustration"
          />
        </Flex>
        <Flex
          sx={{
            position: 'absolute',
            display: ['flex', 'none', 'flex'],
            zIndex: 'zIndex0',
            pointerEvents: 'none',
            top: ['1rem', '13rem'],
            width: '100%',
            height: '32rem',
            flexShrink: '0',
            borderRadius: '49.5rem',
            background: 'aquamarine8',
            filter: 'blur(150px)',
          }}
        />
      </StarsWrapper>
    </Flex>
  )
}
